import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import DistributionLine from '../../../../static/outageIconsV2/distribution_line.svg';
import ServiceLocation from '../../../../static/outageIconsV2/house.svg';
import OutageStatusGreen from '../../../../static/outageIconsV2/outage_status_green.svg';
import OutageStatusRed from '../../../../static/outageIconsV2/outage_status_red.svg';
import Substation from '../../../../static/outageIconsV2/substation.svg';
import Transformer from '../../../../static/outageIconsV2/transformer.svg';
import colors from '../../../../themes/main-colors';
import { FullDivider } from '../../../pge-plus-common/FullDivider';
import { CustomDialog } from '../common/custom-dialog';
import HelpIcon from '../icons/HelpIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    helpIcon: {
      fontSize: 30,
      color: colors.white,
    },
    subHeader: {
      fontSize: '1.25rem',
      marginBottom: theme.spacing(1),
      fontWeight: 600,
    },
    body: {
      padding: theme.spacing(2),
    },
    powerIndicators: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1),
      background: colors.white,
      display: 'flex',
    },
    powerIndicatorItem: {
      marginRight: theme.spacing(3),
      display: 'flex',
    },
    powerIndicatorItemIcon: {
      marginRight: theme.spacing(2),
    },
    accordion: {
      marginBottom: theme.spacing(2),
      background: colors.white,
      borderRadius: 0,
      boxShadow: 'none',
      '&::before': {
        background: 'none',
      },
    },
    accordionSummary: {
      '& > div.Mui-expanded': {
        marginBottom: 0,
      },
    },
    accordionDetails: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 0,
      '& p': {
        padding: theme.spacing(1),
      },
    },
  }),
);

interface PowerStatusDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const PowerStatusDialog = ({
  open,
  setOpen,
}: PowerStatusDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const assetTypes = useMemo(
    () => [
      {
        summaryIcon: <ServiceLocation width={24} height={24} />,
        summaryTitle: t('SERVICE_LOCATION_2'),
        details: t('SERVICE_LOCATION_DETAILS'),
      },
      {
        summaryIcon: <Transformer width={24} height={24} />,
        summaryTitle: t('TRANSFORMER'),
        details: t('TRANSFORMER_DETAILS'),
      },
      {
        summaryIcon: <DistributionLine width={24} height={24} />,
        summaryTitle: t('DISTRIBUTION_LINE'),
        details: t('DISTRIBUTION_LINE_DETAILS'),
      },
      {
        summaryIcon: <Substation width={24} height={24} />,
        summaryTitle: t('SUBSTATION'),
        details: t('SUBSTATION_DETAILS'),
      },
    ],
    [],
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={t('POWER_STATUS')}
        titleIcon={<HelpIcon className={classes.helpIcon} />}
      >
        <Grid className={classes.body}>
          <Grid>
            <Typography variant="h6" className={classes.subHeader}>
              {t('POWER_INDICATORS')}
            </Typography>
            <Grid className={classes.powerIndicators}>
              <Grid className={classes.powerIndicatorItem}>
                <OutageStatusGreen
                  width={24}
                  height={24}
                  className={classes.powerIndicatorItemIcon}
                />
                <Typography>{t('POWER_ON')}</Typography>
              </Grid>
              <Grid className={classes.powerIndicatorItem}>
                <OutageStatusRed
                  width={24}
                  height={24}
                  className={classes.powerIndicatorItemIcon}
                />
                <Typography>{t('POWER_ISSUE')}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Typography variant="h6" className={classes.subHeader}>
              {t('ASSET_TYPES')}
            </Typography>
            <Grid>
              {assetTypes.map((assetType, i) => (
                <Accordion key={i} className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Grid>
                      {assetType.summaryIcon}
                      <Typography variant="h6" className={classes.subHeader}>
                        {assetType.summaryTitle}
                      </Typography>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <FullDivider />
                    <Typography>{assetType.details}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CustomDialog>
    </Fragment>
  );
};
